import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import './DiaryNew.css';
import { getEventsForScheduler, updateEvent, removeSchedulerJob, submitEvent, updateJobDescription, getBookingRun, deleteBookingRun, BookingRun } from '../../data/jobData';
import { addNote } from '../../data/notes';
import { getEmployeesForDiary, getEmployeeDropdown } from '../../data/employeeData';
import { getDepartmentDropdown } from '../../data/departmentData';
import { getJobsForSchedulerList } from '../../data/jobData';
import { dropDown } from '../../data/interfaces';
import { useToast } from '../../contexts/ToastContext';
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { DateTimePicker, DatePicker } from '@progress/kendo-react-dateinputs';
import { Input, Switch, RadioGroup } from '@progress/kendo-react-inputs';
import { FaCalendar, FaFilter, FaTimes, FaTrash, FaEdit, FaClock, FaMapMarkerAlt, FaUser, FaBriefcase, FaChevronLeft, FaChevronRight, FaChevronDown, FaPlus } from 'react-icons/fa';
import EventContainerList, { GridDataItem } from '../../components/Diary/EventContainerList';
import { isBankHoliday } from '../../helpers/ukBankHolidays';
import { useDiaryLiveUpdates } from '../../helpers/useDiaryLiveUpdates';

interface ScheduledEvent {
  id: number | undefined;
  jobId: number | undefined;
  jobRef?: string | undefined;
  title: string | undefined;
  siteAddr: string | undefined;
  projectManager: string | undefined;
  client: string | undefined;
  description: string | undefined;
  start: Date;
  end: Date;
  employeeIds?: number[] | number;
}

interface ResourceData {
  employeeId: number;
  employeeName: string;
  department: string;
  departmentColourHex: string;
}

interface CalendarEvent {
  id: number;
  jobId: number;
  jobRef: string;
  title: string;
  start: Date;
  end: Date;
  siteAddr: string;
  projectManager: string;
  client: string;
  description: string;
  employeeIds: number[];
  color: string;
  createdBy: string | null;
  createdDate: Date | null;
  modifiedBy: string | null;
  modifiedDate: Date | null;
}

interface DiaryNewProps {
  lockEdit: boolean;
}

// Entries created before the appointment audit columns existed have no values
// recorded, and an entry that has never been edited has no modified values.
const formatAudit = (name: string | null, date: Date | null): string => {
  if (!name && !date) return '-';
  const who = name || 'Unknown';
  return date ? `${who}, ${moment(date).format('DD/MM/YYYY HH:mm')}` : who;
};

const DiaryNew: React.FC<DiaryNewProps> = ({ lockEdit }) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [resources, setResources] = useState<ResourceData[]>([]);
  const [filteredEmployeeIds, setFilteredEmployeeIds] = useState<number[]>([]);
  const [filteredDepartments, setFilteredDepartments] = useState<string[]>([]);
  const [jobNumberFilter, setJobNumberFilter] = useState<string>('');
  const [departments, setDepartments] = useState<dropDown[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(moment().startOf('week').toDate());
  const [daysToShow, setDaysToShow] = useState(7);
  const [hoveredEvent, setHoveredEvent] = useState<CalendarEvent | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number; rectBottom: number } | null>(null);
  
  // Event editing state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editStartDate, setEditStartDate] = useState<Date | null>(null);
  const [editEndDate, setEditEndDate] = useState<Date | null>(null);
  const [editEmployees, setEditEmployees] = useState<{ text: string; value: number }[]>([]);
  const [editDescription, setEditDescription] = useState<string>('');

  // Add note state (within the edit job dialog)
  const [noteText, setNoteText] = useState<string>('');
  const [noteEmployees, setNoteEmployees] = useState<{ text: string; value: number }[]>([]);
  const [noteEmployeeOptions, setNoteEmployeeOptions] = useState<{ text: string; value: number }[]>([]);
  const [savingNote, setSavingNote] = useState(false);
  const [noteSectionExpanded, setNoteSectionExpanded] = useState(false);

  // Delete confirmation
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<CalendarEvent | null>(null);
  // 'single' deletes just the chosen entry; 'booking' also deletes the rest of
  // the booking it belongs to, from today onwards.
  const [deleteMode, setDeleteMode] = useState<'single' | 'booking'>('single');
  const [bookingRun, setBookingRun] = useState<BookingRun | null>(null);
  const [bookingRunLoading, setBookingRunLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Create event dialog
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [createStartDate, setCreateStartDate] = useState<Date | null>(null);
  const [createEndDate, setCreateEndDate] = useState<Date | null>(null);
  const [createEmployeeId, setCreateEmployeeId] = useState<number | null>(null);
  const [availableJobs, setAvailableJobs] = useState<GridDataItem[]>([]);
  const [selectedJob, setSelectedJob] = useState<GridDataItem | null>(null);
  const [isCreateAllDay, setIsCreateAllDay] = useState(false);
  const [createIncludesWeekends, setCreateIncludesWeekends] = useState(false);

  const { showToast } = useToast();

  // Whether the entry runs over more than one day with a Saturday or Sunday in
  // it - only then is there a choice to make about weekends.
  const createSpansWeekend = useMemo(() => {
    if (!createStartDate || !createEndDate) return false;
    const first = moment(createStartDate).startOf('day');
    const last = moment(createEndDate).startOf('day');
    if (!last.isAfter(first)) return false;
    for (const day = first.clone(); !day.isAfter(last); day.add(1, 'day')) {
      if (day.day() === 0 || day.day() === 6) return true;
    }
    return false;
  }, [createStartDate, createEndDate]);

  // Handle all day toggle for create dialog
  useEffect(() => {
    if (isCreateAllDay && createStartDate) {
      const startWith0730 = new Date(createStartDate);
      startWith0730.setHours(7, 30, 0, 0);
      setCreateStartDate(startWith0730);
      
      const endWith1630 = new Date(createStartDate);
      endWith1630.setHours(16, 30, 0, 0);
      setCreateEndDate(endWith1630);
    }
  }, [isCreateAllDay]);

  // Helper function to determine if a color is light
  const isLightColor = (color: string): boolean => {
    // Normalise the hex string, expanding shorthand (#fff -> #ffffff)
    let hex = (color || '').replace('#', '').trim();
    if (hex.length === 3) {
      hex = hex.split('').map(ch => ch + ch).join('');
    }

    // If we can't parse a 6-digit hex, treat it as light so we fall back to
    // dark text (readable on the default/white background)
    if (hex.length !== 6) return true;

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return true;

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return true if luminance is greater than 0.5 (light color)
    return luminance > 0.5;
  };

  // Build a two-letter initial from an employee name (e.g. "James Phillips" -> "JP")
  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Resolve a set of employee ids into displayable crew info
  const getCrew = useCallback((employeeIds: number[]) => {
    return employeeIds
      .map(id => resources.find(r => r.employeeId === id))
      .filter((r): r is ResourceData => !!r)
      .map(r => ({
        id: r.employeeId,
        name: r.employeeName,
        initials: getInitials(r.employeeName),
        color: r.departmentColourHex || '#6c757d',
      }));
  }, [resources]);

  // Aggregate every employee scheduled on a given job on a given day, so a job's facepile
  // shows the crew booked that day rather than everyone who has ever worked the job.
  const employeeIdsByJobDay = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const ev of events) {
      const key = `${ev.jobId}|${moment(ev.start).format('YYYY-MM-DD')}`;
      const ids = map.get(key) ?? [];
      for (const id of ev.employeeIds) {
        if (!ids.includes(id)) ids.push(id);
      }
      map.set(key, ids);
    }
    return map;
  }, [events]);

  // Resolve the crew scheduled on the job that day for the given event
  const getJobCrew = useCallback((jobId: number, date: Date) => {
    const key = `${jobId}|${moment(date).format('YYYY-MM-DD')}`;
    return getCrew(employeeIdsByJobDay.get(key) ?? []);
  }, [getCrew, employeeIdsByJobDay]);

  // Fetch events
  const fetchEvents = useCallback(async (filterIds?: number[]) => {
    try {
      const scheduledData = await getEventsForScheduler(false, false, filterIds);
      
      const calendarEvents: CalendarEvent[] = scheduledData
        .map((event) => {
          // Normalize employeeIds to array
          const employeeIds = Array.isArray(event.employeeIds) 
            ? event.employeeIds 
            : event.employeeIds 
              ? [event.employeeIds] 
              : [];
              
          // Use light neutral color for all events for better readability
          const color = '#e8e8e8';

          return {
            id: event.id ?? 0,
            jobId: event.jobId ?? 0,
            jobRef: event.jobRef ?? '',
            title: event.title ?? '',
            start: new Date(event.start),
            end: new Date(event.end),
            siteAddr: event.siteAddr ?? '',
            projectManager: event.projectManager ?? '',
            client: event.client ?? '',
            description: event.description ?? '',
            employeeIds: employeeIds,
            color: color,
            createdBy: event.createdBy ?? null,
            createdDate: event.createdDate ? new Date(event.createdDate) : null,
            modifiedBy: event.modifiedBy ?? null,
            modifiedDate: event.modifiedDate ? new Date(event.modifiedDate) : null
          };
        });

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      showToast('Failed to fetch diary events', 'error');
    }
  }, [resources, showToast]);

  // Fetch resources
  const fetchResources = useCallback(async (filterIds?: number[]) => {
    try {
      const resourceData = await getEmployeesForDiary(true, filterIds);
      setResources(resourceData);
    } catch (error) {
      console.error('Error fetching resources:', error);
      showToast('Failed to fetch employees', 'error');
    }
  }, [showToast]);

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    try {
      const deptData = await getDepartmentDropdown();
      setDepartments(deptData);
    } catch (error) {
      console.error('Error loading departments:', error);
      showToast('Failed to load departments', 'error');
    }
  }, [showToast]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchResources();
      await fetchDepartments();
      await fetchEvents();
      setLoading(false);
    };
    loadData();
  }, []);

  // Reload events when resources change
  useEffect(() => {
    if (resources.length > 0 && !loading) {
      fetchEvents(filteredEmployeeIds.length > 0 ? filteredEmployeeIds : undefined);
    }
  }, [resources, filteredEmployeeIds, fetchEvents, loading]);

  // Re-fetch honouring whatever employee filter is currently applied
  const refreshEvents = useCallback(() => {
    fetchEvents(filteredEmployeeIds.length > 0 ? filteredEmployeeIds : undefined);
  }, [fetchEvents, filteredEmployeeIds]);

  // Pick up schedule changes made by other users without a page refresh
  useDiaryLiveUpdates(refreshEvents);

  // Handle employee filter change
  const handleFilterChange = (event: MultiSelectChangeEvent) => {
    const selectedIds = event.value.map((item: any) => Number(item.value));
    setFilteredEmployeeIds(selectedIds);
    // Don't filter resources - always show all employees
    // Only filter the events
    fetchEvents(selectedIds.length > 0 ? selectedIds : undefined);
  };

  // Handle department filter change
  const handleDepartmentFilterChange = (event: MultiSelectChangeEvent) => {
    const selectedDepts = event.value.map((item: { text: string }) => item.text);
    setFilteredDepartments(selectedDepts);
  };

  // Employee dropdown options
  const employeeOptions = useMemo(() => 
    resources
      .map(r => ({ text: r.employeeName, value: r.employeeId }))
      .sort((a, b) => a.text.localeCompare(b.text)),
    [resources]
  );

  const [headerEmployeeOptions, setHeaderEmployeeOptions] = useState<{ text: string; value: number }[]>([]);
  const [editEmployeeOptions, setEditEmployeeOptions] = useState<{ text: string; value: number }[]>([]);

  useEffect(() => {
    setHeaderEmployeeOptions(employeeOptions);
    setEditEmployeeOptions(employeeOptions);
  }, [employeeOptions]);

  // The note "assigned employees" dropdown mirrors the job screen's note form: it uses the
  // full employee dropdown (all users), not just the diary's schedulable resources.
  useEffect(() => {
    getEmployeeDropdown('')
      .then(setNoteEmployeeOptions)
      .catch(() => showToast('Failed to load employees', 'error'));
  }, [showToast]);

  const handleHeaderEmployeeSearch = (event: MultiSelectFilterChangeEvent) => {
    const val = event.filter.value.toLowerCase();
    setHeaderEmployeeOptions(
      val ? employeeOptions.filter(e => e.text.toLowerCase().includes(val)) : employeeOptions
    );
  };

  const handleEditEmployeeSearch = (event: MultiSelectFilterChangeEvent) => {
    const val = event.filter.value.toLowerCase();
    setEditEmployeeOptions(
      val ? employeeOptions.filter(e => e.text.toLowerCase().includes(val)) : employeeOptions
    );
  };

  const handleNoteEmployeeSearch = async (event: MultiSelectFilterChangeEvent) => {
    try {
      const results = await getEmployeeDropdown(event.filter.value || '');
      setNoteEmployeeOptions(results);
    } catch {
      showToast('Failed to search employees', 'error');
    }
  };

  // Handle event save from dialog
  const handleSaveEvent = async () => {
    if (!selectedEvent || !editStartDate || !editEndDate) return;

    try {
      const employeeIds = editEmployees.map(e => e.value);
      await updateEvent(selectedEvent.id, editStartDate, editEndDate, employeeIds);

      // Persist the job description if it has changed
      if (selectedEvent.jobId && editDescription !== selectedEvent.description) {
        await updateJobDescription(selectedEvent.jobId, editDescription);
      }

      showToast('Job updated successfully', 'success');
      setEditDialogVisible(false);
      setSelectedEvent(null);
      fetchEvents(filteredEmployeeIds.length > 0 ? filteredEmployeeIds : undefined);
    } catch (error) {
      console.error('Error saving event:', error);
      showToast('Failed to save job', 'error');
    }
  };

  // Add a note against the job currently shown in the edit dialog
  const handleAddNote = async () => {
    if (!selectedEvent?.jobId) return;

    if (noteText.trim() === '') {
      showToast('Please enter a note', 'error');
      return;
    }

    if (noteEmployees.length === 0) {
      showToast('Please assign at least one employee to the note', 'error');
      return;
    }

    setSavingNote(true);
    try {
      await addNote(
        { enquiryId: null, quoteId: null, jobId: selectedEvent.jobId },
        noteText,
        noteEmployees.map(e => e.value) as unknown as number,
      );
      showToast('Note successfully added', 'success');
      setNoteText('');
      setNoteEmployees([]);
      setNoteSectionExpanded(false);
    } catch (error) {
      console.error('Error adding note:', error);
      showToast('An error occurred creating a new note', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogVisible(false);
    setEventToDelete(null);
    setDeleteMode('single');
    setBookingRun(null);
  };

  // Handle event delete
  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    setDeleting(true);
    try {
      if (deleteMode === 'booking') {
        const deleted = await deleteBookingRun(eventToDelete.id);
        showToast(`${deleted} ${deleted === 1 ? 'entry' : 'entries'} deleted`, 'success');
      } else {
        await removeSchedulerJob(eventToDelete.id);
        showToast('Event deleted successfully', 'success');
      }
      closeDeleteDialog();
      fetchEvents(filteredEmployeeIds.length > 0 ? filteredEmployeeIds : undefined);
    } catch (error) {
      console.error('Error deleting event:', error);
      showToast('Failed to delete event', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Looked up when the whole-booking option is picked, so the dialog can list
  // exactly which days will go before anything is deleted.
  const handleDeleteModeChange = async (mode: 'single' | 'booking') => {
    setDeleteMode(mode);
    if (mode !== 'booking' || !eventToDelete || bookingRun) return;

    setBookingRunLoading(true);
    try {
      setBookingRun(await getBookingRun(eventToDelete.id));
    } catch (error) {
      console.error('Error loading booking:', error);
      showToast('Could not work out the rest of this booking', 'error');
      setDeleteMode('single');
    } finally {
      setBookingRunLoading(false);
    }
  };

  // Open delete confirmation dialog
  const confirmDeleteEvent = (event: CalendarEvent) => {
    setEventToDelete(event);
    setDeleteMode('single');
    setBookingRun(null);
    setDeleteDialogVisible(true);
  };

  // Generate array of dates to display
  const dateRange = useMemo(() => {
    const dates = [];
    for (let i = 0; i < daysToShow; i++) {
      dates.push(moment(startDate).add(i, 'days').toDate());
    }
    return dates;
  }, [startDate, daysToShow]);

  // Get events for a specific employee and date
  const getEventsForEmployeeAndDate = useCallback((employeeId: number, date: Date) => {
    const dayStart = moment(date).startOf('day');
    const dayEnd = moment(date).endOf('day');
    
    let filteredEvents = events.filter(event => 
      event.employeeIds.includes(employeeId) &&
      moment(event.start).isBetween(dayStart, dayEnd, null, '[]')
    );
    
    // Apply job number filter if specified
    if (jobNumberFilter.trim() !== '') {
      filteredEvents = filteredEvents.filter(event => 
        event.jobRef.toLowerCase().includes(jobNumberFilter.toLowerCase())
      );
    }
    
    return filteredEvents.sort((a, b) => moment(a.start).diff(moment(b.start)));
  }, [events, jobNumberFilter]);

  // Navigation handlers
  const handlePreviousWeek = () => {
    setStartDate(moment(startDate).subtract(7, 'days').toDate());
  };

  const handleNextWeek = () => {
    setStartDate(moment(startDate).add(7, 'days').toDate());
  };

  const handleToday = () => {
    setStartDate(moment().startOf('week').toDate());
  };

  // Handle event click
  const handleEventClick = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
    setEditDialogVisible(true);
    setEditStartDate(event.start);
    setEditEndDate(event.end);
    setEditDescription(event.description ?? '');
    setNoteText('');
    setNoteEmployees([]);
    setNoteSectionExpanded(false);
    setEditEmployees(
      resources
        .filter(r => event.employeeIds.includes(r.employeeId))
        .map(r => ({ text: r.employeeName, value: r.employeeId }))
    );
  }, [resources]);

  // Handle cell double-click to create event
  const handleCellDoubleClick = useCallback(async (employeeId: number, date: Date, event: React.MouseEvent<HTMLDivElement>) => {
    // Calculate time based on click position within the cell
    const cellElement = event.currentTarget;
    const rect = cellElement.getBoundingClientRect();
    const clickY = event.clientY - rect.top;
    const cellHeight = rect.height;
    const clickPercentage = clickY / cellHeight;
    
    // Working hours: 7am to 6pm (11 hours)
    const workStartHour = 7;
    const workHours = 11;
    const totalMinutes = workHours * 60;
    const clickedMinutes = Math.floor(clickPercentage * totalMinutes);
    const clickedHour = Math.floor(clickedMinutes / 60) + workStartHour;
    const clickedMinute = clickedMinutes % 60;
    
    // Round to nearest 15 minutes
    const roundedMinute = Math.round(clickedMinute / 15) * 15;
    
    // Create start time
    const startTime = moment(date)
      .hours(clickedHour)
      .minutes(roundedMinute)
      .seconds(0)
      .toDate();
    
    // Default end time is 1 hour later
    const endTime = moment(startTime).add(1, 'hour').toDate();
    
    // Fetch available jobs
    try {
      const jobs = await getJobsForSchedulerList(false, false);
      setAvailableJobs(jobs);
    } catch (error) {
      console.error('Error loading jobs:', error);
      showToast('Failed to load jobs', 'error');
      return;
    }
    
    setCreateEmployeeId(employeeId);
    setCreateStartDate(startTime);
    setCreateEndDate(endTime);
    setSelectedJob(null);
    setCreateDialogVisible(true);
  }, [showToast]);

  // Handle create event submit
  const handleCreateEvent = async () => {
    if (!selectedJob || !createStartDate || !createEndDate || !createEmployeeId) {
      showToast('Please select a job and set start/end times', 'error');
      return;
    }

    // Only a choice when the entry spans a weekend; otherwise book every day.
    const includeWeekends = !createSpansWeekend || createIncludesWeekends;
    if (!includeWeekends) {
      const last = moment(createEndDate).startOf('day');
      let hasWeekday = false;
      for (const day = moment(createStartDate).startOf('day'); !day.isAfter(last); day.add(1, 'day')) {
        if (day.day() !== 0 && day.day() !== 6) hasWeekday = true;
      }
      if (!hasWeekday) {
        showToast('Those dates are all at the weekend - turn on "Includes weekends" to book them', 'error');
        return;
      }
    }

    try {
      const startDateStr = moment(createStartDate).format('YYYY-MM-DDTHH:mm:ss');
      const endDateStr = moment(createEndDate).format('YYYY-MM-DDTHH:mm:ss');
      
      await submitEvent(selectedJob.jobId, startDateStr, endDateStr, [createEmployeeId], includeWeekends);
      showToast('Event created successfully', 'success');
      setCreateDialogVisible(false);
      setIsCreateAllDay(false);
      setCreateIncludesWeekends(false);
      fetchEvents(filteredEmployeeIds.length > 0 ? filteredEmployeeIds : undefined);
    } catch (error) {
      console.error('Error creating event:', error);
      showToast('Failed to create event', 'error');
    }
  };

  // Calculate display resources (must be before early return)
  const displayResources = useMemo(() => {
    let filtered = resources;
    
    // Filter by employee if any selected
    if (filteredEmployeeIds.length > 0) {
      filtered = filtered.filter(r => filteredEmployeeIds.includes(r.employeeId));
    }
    
    // Filter by department if any selected
    if (filteredDepartments.length > 0) {
      filtered = filtered.filter(r => filteredDepartments.includes(r.department));
    }
    
    // Filter by job number if specified
    if (jobNumberFilter.trim() !== '') {
      // Get employee IDs that have events matching the job number filter
      const matchingEmployeeIds = new Set(
        events
          .filter(event => event.jobRef.toLowerCase().includes(jobNumberFilter.toLowerCase()))
          .flatMap(event => event.employeeIds)
      );
      filtered = filtered.filter(r => matchingEmployeeIds.has(r.employeeId));
    }
    
    return filtered;
  }, [resources, filteredEmployeeIds, filteredDepartments, jobNumberFilter, events]);

  if (loading) {
    return (
      <div className="diary-container">
        <div className="diary-header">
          <h1 className="diary-title">
            <FaCalendar /> Diary
          </h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading diary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="diary-container">
      <div className="diary-header">
        <h1 className="diary-title">
          <FaCalendar /> Diary
        </h1>
        
        <div className="diary-filters">
          <div className="filter-group">
            <label>
              <FaCalendar /> Jump to Date
            </label>
            <DatePicker
              value={startDate}
              onChange={(e) => e.value && setStartDate(moment(e.value).startOf('week').toDate())}
              format="dd/MM/yyyy"
              style={{ width: '200px' }}
            />
          </div>
          
          <div className="filter-group">
            <label>
              <FaFilter /> Filter by Employee
            </label>
            <MultiSelect
              data={headerEmployeeOptions}
              textField="text"
              dataItemKey="value"
              placeholder="All Employees"
              value={employeeOptions.filter(opt => filteredEmployeeIds.includes(opt.value))}
              onChange={handleFilterChange}
              filterable={true}
              onFilterChange={handleHeaderEmployeeSearch}
              style={{ width: '300px' }}
            />
          </div>
          
          <div className="filter-group">
            <label>
              <FaBriefcase /> Filter by Department
            </label>
            <MultiSelect
              data={departments}
              textField="text"
              dataItemKey="text"
              placeholder="All Departments"
              value={departments.filter(dept => filteredDepartments.includes(dept.text))}
              onChange={handleDepartmentFilterChange}
              style={{ width: '300px' }}
            />
          </div>
          
          <div className="filter-group">
            <label>
              <FaFilter /> Job Number
            </label>
            <Input
              value={jobNumberFilter}
              onChange={(e) => setJobNumberFilter(e.value || '')}
              placeholder="Search by job reference..."
              style={{ width: '250px' }}
            />
          </div>
        </div>
      </div>

      <div className="diary-navigation">
        <Button onClick={handlePreviousWeek} icon="arrow-left">
          <FaChevronLeft /> Previous Week
        </Button>
        <Button onClick={handleToday}>
          Today
        </Button>
        <Button onClick={handleNextWeek}>
          Next Week <FaChevronRight />
        </Button>
      </div>

      <div className="diary-grid-wrapper">
        <table className="diary-grid">
          <thead>
            <tr>
              <th className="employee-header">Employee</th>
              <th className="time-scale-header">Time</th>
              {dateRange.map(date => {
                const holiday = isBankHoliday(date);
                return (
                  <th 
                    key={date.toISOString()} 
                    className={`date-header ${holiday ? 'bank-holiday-header' : ''}`}
                  >
                    <div className="date-header-content">
                      <div className="day-name">{moment(date).format('ddd')}</div>
                      <div className="day-date">{moment(date).format('DD MMM')}</div>
                      {holiday && (
                        <div className="bank-holiday-label">{holiday.name}</div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayResources.map(employee => (
              <tr key={employee.employeeId}>
                <td className="employee-cell">
                  <div className="employee-name">
                    <span 
                      className="department-color-pill" 
                      style={{ 
                        backgroundColor: employee.departmentColourHex,
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        marginRight: '8px',
                        verticalAlign: 'middle'
                      }}
                    />
                    {employee.employeeName}
                  </div>
                  <div className="employee-department">{employee.department}</div>
                </td>
                <td className="time-scale-cell">
                  <div className="time-scale">
                    {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(hour => (
                      <div key={hour} className="time-label">{hour}:00</div>
                    ))}
                  </div>
                </td>
                {dateRange.map(date => {
                  const dayEvents = getEventsForEmployeeAndDate(employee.employeeId, date);
                  const holiday = isBankHoliday(date);
                  
                  // Calculate positioning for time-based layout
                  // Working hours: 7am (7) to 6pm (18) = 11 hour span
                  const workStartHour = 7;
                  const workEndHour = 18;
                  const workHours = workEndHour - workStartHour;
                  
                  const calculatePosition = (time: Date) => {
                    const hours = moment(time).hours();
                    const minutes = moment(time).minutes();
                    const totalMinutes = (hours - workStartHour) * 60 + minutes;
                    const workMinutes = workHours * 60;
                    return Math.max(0, Math.min(100, (totalMinutes / workMinutes) * 100));
                  };
                  
                  const calculateHeight = (start: Date, end: Date) => {
                    const durationMinutes = moment(end).diff(moment(start), 'minutes');
                    const workMinutes = workHours * 60;
                    const calculatedHeight = (durationMinutes / workMinutes) * 100;
                    // Minimum height for very short events (ensures visibility)
                    return Math.max(3, calculatedHeight);
                  };
                  
                  // Detect overlaps and assign columns
                  const eventsWithLayout = dayEvents.map((event, index) => {
                    let column = 0;
                    let totalColumns = 1;
                    
                    // Check for overlaps with previous events
                    for (let i = 0; i < index; i++) {
                      const prevEvent = dayEvents[i];
                      const eventStart = moment(event.start);
                      const eventEnd = moment(event.end);
                      const prevStart = moment(prevEvent.start);
                      const prevEnd = moment(prevEvent.end);
                      
                      // Check if events overlap
                      if (eventStart.isBefore(prevEnd) && eventEnd.isAfter(prevStart)) {
                        column++;
                      }
                    }
                    
                    // Count max columns needed
                    let maxColumn = column;
                    for (let i = index; i < dayEvents.length; i++) {
                      const otherEvent = dayEvents[i];
                      const eventStart = moment(event.start);
                      const eventEnd = moment(event.end);
                      const otherStart = moment(otherEvent.start);
                      const otherEnd = moment(otherEvent.end);
                      
                      if (eventStart.isBefore(otherEnd) && eventEnd.isAfter(otherStart)) {
                        let otherColumn = 0;
                        for (let j = 0; j < i; j++) {
                          const checkEvent = dayEvents[j];
                          const checkStart = moment(checkEvent.start);
                          const checkEnd = moment(checkEvent.end);
                          if (otherStart.isBefore(checkEnd) && otherEnd.isAfter(checkStart)) {
                            otherColumn++;
                          }
                        }
                        maxColumn = Math.max(maxColumn, otherColumn);
                      }
                    }
                    totalColumns = maxColumn + 1;
                    
                    return { ...event, column, totalColumns };
                  });
                  
                  return (
                    <td 
                      key={`${employee.employeeId}-${date.toISOString()}`} 
                      className={`event-cell ${holiday ? 'bank-holiday-cell' : ''}`}
                    >
                      <div 
                        className="event-cell-timeline"
                        onDoubleClick={(e) => handleCellDoubleClick(employee.employeeId, date, e)}
                      >
                        {eventsWithLayout.map((event) => {
                          const top = calculatePosition(event.start);
                          const height = calculateHeight(event.start, event.end);
                          const width = 100 / event.totalColumns;
                          const left = (event.column * width);
                          const textColor = isLightColor(event.color) ? '#000000' : '#ffffff';
                          
                          return (
                            <div
                              key={event.id}
                              className="event-item-positioned"
                              style={{ 
                                backgroundColor: event.color,
                                color: textColor,
                                top: `${top}%`,
                                height: `${height}%`,
                                left: `${left}%`,
                                width: `${width}%`
                              }}
                              onClick={() => handleEventClick(event)}                              onMouseEnter={(e) => {
                                setHoveredEvent(event);
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPosition({ 
                                  x: rect.left + rect.width / 2, 
                                  y: rect.top,
                                  rectBottom: rect.bottom
                                });
                              }}
                              onMouseLeave={() => {
                                setHoveredEvent(null);
                                setTooltipPosition(null);

                              }}
                            >
                              <div className="event-item-header">
                                <div className="event-item-time">
                                  <FaClock size={10} /> {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
                                </div>
                                {(() => {
                                  const crew = getJobCrew(event.jobId, event.start);
                                  if (crew.length === 0) return null;
                                  const maxChips = 4;
                                  const shown = crew.slice(0, maxChips);
                                  const overflow = crew.length - shown.length;
                                  // Hovering any chip shows the whole crew, not just that one person
                                  const crewNames = crew.map(m => m.name).join(', ');
                                  return (
                                    <div className="event-item-crew" title={crewNames}>
                                      {shown.map(member => (
                                        <span
                                          key={member.id}
                                          className="crew-chip"
                                          style={{
                                            backgroundColor: member.color,
                                            color: isLightColor(member.color) ? '#000000' : '#ffffff',
                                          }}
                                          title={crewNames}
                                        >
                                          {member.initials}
                                        </span>
                                      ))}
                                      {overflow > 0 && (
                                        <span className="crew-chip crew-chip-overflow" title={crewNames}>+{overflow}</span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="event-item-title">{event.title}</div>
                              <div className="event-item-client">{event.client}</div>
                            </div>
                          );
                        })}
                        
                        {/* Current time indicator - only show if this is today */}
                        {moment(date).isSame(moment(), 'day') && (() => {
                          const now = new Date();
                          const currentHour = now.getHours();
                          const currentMinute = now.getMinutes();
                          
                          // Only show if within working hours
                          if (currentHour >= workStartHour && currentHour < workEndHour) {
                            const currentPosition = calculatePosition(now);
                            return (
                              <div 
                                className="current-time-indicator" 
                                style={{ top: `${currentPosition}%` }}
                                title={`Current time: ${moment(now).format('HH:mm')}`}
                              >
                                <div className="current-time-line"></div>
                                <div className="current-time-dot"></div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Event Edit/View Dialog */}
      {editDialogVisible && selectedEvent && (
        <Dialog
          title={`${lockEdit ? 'View Job' : 'Edit Job'}${selectedEvent.jobRef ? ` ${selectedEvent.jobRef}` : ''}`}
          onClose={() => {
            setEditDialogVisible(false);
            setSelectedEvent(null);
          }}
          width={600}
        >
          <div className="event-dialog-content">
            <div className="event-detail-row">
              <FaBriefcase className="detail-icon" />
              <div>
                <strong>Job:</strong> {selectedEvent.title}
              </div>
            </div>

            <div className="event-detail-row">
              <FaMapMarkerAlt className="detail-icon" />
              <div>
                <strong>Site:</strong> {selectedEvent.siteAddr}
              </div>
            </div>

            <div className="event-detail-row">
              <FaUser className="detail-icon" />
              <div>
                <strong>Client:</strong> {selectedEvent.client}
              </div>
            </div>

            <div className="event-detail-row">
              <FaUser className="detail-icon" />
              <div>
                <strong>Project Manager:</strong> {selectedEvent.projectManager}
              </div>
            </div>

            <div className="event-detail-row">
              <FaPlus className="detail-icon" />
              <div>
                <strong>Allocated:</strong> {formatAudit(selectedEvent.createdBy, selectedEvent.createdDate)}
              </div>
            </div>

            <div className="event-detail-row">
              <FaEdit className="detail-icon" />
              <div>
                <strong>Modified:</strong> {formatAudit(selectedEvent.modifiedBy, selectedEvent.modifiedDate)}
              </div>
            </div>

            <div className="form-field">
              <label>Description</label>
              {lockEdit ? (
                <div>{editDescription || '-'}</div>
              ) : (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              )}
            </div>

            <div className="form-field">
              <label>
                <FaClock /> Start Date & Time
              </label>
              <DateTimePicker
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.value)}
                disabled={lockEdit}
                format="dd/MM/yyyy HH:mm"
              />
            </div>

            <div className="form-field">
              <label>
                <FaClock /> End Date & Time
              </label>
              <DateTimePicker
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.value)}
                disabled={lockEdit}
                format="dd/MM/yyyy HH:mm"
              />
            </div>

            <div className="form-field">
              <label>
                <FaUser /> Assigned Employees
              </label>
              <MultiSelect
                data={editEmployeeOptions}
                textField="text"
                dataItemKey="value"
                value={editEmployees}
                onChange={(e) => setEditEmployees(e.value)}
                disabled={lockEdit}
                filterable={true}
                onFilterChange={handleEditEmployeeSearch}
              />
            </div>

            {!lockEdit && (
              <div className="add-note-section" style={{ borderTop: '1px solid #e0e0e0', marginTop: '1em', paddingTop: '0.5em' }}>
                <button
                  type="button"
                  className="add-note-toggle"
                  onClick={() => setNoteSectionExpanded(prev => !prev)}
                  aria-expanded={noteSectionExpanded}
                >
                  {noteSectionExpanded ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                  <span>Add Note</span>
                </button>

                {noteSectionExpanded && (
                  <div className="add-note-body">
                    <div className="form-field">
                      <label>Note</label>
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        rows={3}
                        style={{ width: '100%', resize: 'vertical' }}
                      />
                    </div>

                    <div className="form-field" style={{ marginTop: '0.75em' }}>
                      <label>
                        <FaUser /> Assigned Employees
                      </label>
                      <MultiSelect
                        data={noteEmployeeOptions}
                        textField="text"
                        dataItemKey="value"
                        value={noteEmployees}
                        onChange={(e) => setNoteEmployees(e.value)}
                        filterable={true}
                        onFilterChange={handleNoteEmployeeSearch}
                      />
                    </div>

                    <div style={{ marginTop: '1em' }}>
                      <Button
                        themeColor="primary"
                        onClick={handleAddNote}
                        disabled={savingNote}
                        icon="plus"
                      >
                        {savingNote ? 'Adding...' : 'Add Note'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogActionsBar>
            {!lockEdit && (
              <>
                <Button
                  themeColor="error"
                  onClick={() => {
                    setEditDialogVisible(false);
                    confirmDeleteEvent(selectedEvent);
                  }}
                  icon="delete"
                >
                  Delete
                </Button>
                <Button
                  themeColor="primary"
                  onClick={handleSaveEvent}
                  icon="save"
                >
                  Save
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                setEditDialogVisible(false);
                setSelectedEvent(null);
              }}
            >
              {lockEdit ? 'Close' : 'Cancel'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogVisible && eventToDelete && (
        <Dialog
          title="Confirm Delete"
          onClose={closeDeleteDialog}
          width={460}
        >
          <div style={{ padding: '20px' }}>
            <p>Are you sure you want to delete this event?</p>
            <p><strong>{eventToDelete.title}</strong></p>
            <p>
              {moment(eventToDelete.start).format('DD/MM/YYYY HH:mm')} - 
              {moment(eventToDelete.end).format('DD/MM/YYYY HH:mm')}
            </p>

            {eventToDelete.jobId > 0 && (
              <>
                <RadioGroup
                  value={deleteMode}
                  onChange={(e) => handleDeleteModeChange(e.value)}
                  data={[
                    { label: 'Only this entry', value: 'single' },
                    { label: 'This entry and the rest of the booking', value: 'booking' },
                  ]}
                />

                {deleteMode === 'booking' && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      background: '#f5f7fa',
                      border: '1px solid #e1e6ec',
                      fontSize: '13px',
                    }}
                  >
                    <p style={{ margin: '0 0 6px', color: '#555' }}>
                      Deletes this job's entries for the same crew on the days either side, including across
                      weekends and bank holidays. Only entries from today onwards are deleted.
                    </p>
                    {bookingRunLoading || !bookingRun ? (
                      <p style={{ margin: 0 }}>Working out the booking…</p>
                    ) : bookingRun.toDelete.length === 0 ? (
                      <p style={{ margin: 0 }}>
                        <strong>Nothing to delete</strong> – every entry in this booking is before today.
                      </p>
                    ) : (
                      <>
                        <p style={{ margin: '0 0 4px' }}>
                          <strong>
                            {bookingRun.toDelete.length} {bookingRun.toDelete.length === 1 ? 'entry' : 'entries'} will be deleted:
                          </strong>
                        </p>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', maxHeight: '160px', overflowY: 'auto' }}>
                          {bookingRun.toDelete.map((entry) => (
                            <li key={entry.appointmentId}>
                              {moment(entry.startDate).format('ddd DD/MM/YYYY HH:mm')} – {moment(entry.endDate).format('HH:mm')}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {bookingRun && bookingRun.pastEntriesKept > 0 && (
                      <p style={{ margin: '6px 0 0', color: '#555' }}>
                        {bookingRun.pastEntriesKept} earlier {bookingRun.pastEntriesKept === 1 ? 'entry is' : 'entries are'} before
                        today and will be kept.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogActionsBar>
            <Button
              themeColor="error"
              onClick={handleDeleteEvent}
              icon="delete"
              disabled={
                deleting ||
                (deleteMode === 'booking' && (bookingRunLoading || !bookingRun || bookingRun.toDelete.length === 0))
              }
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
            <Button onClick={closeDeleteDialog}>
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Create Event Dialog */}
      {createDialogVisible && (
        <Dialog
          title="Add Job to Diary"
          onClose={() => {
            setCreateDialogVisible(false);
            setSelectedJob(null);
            setIsCreateAllDay(false);
            setCreateIncludesWeekends(false);
          }}
          width={600}
        >
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                <FaBriefcase /> Select Job
              </label>
              {selectedJob ? (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                  <strong>{selectedJob.refFormatted}</strong> - {selectedJob.description}
                  <br />
                  <div style={{ color: 'grey', fontSize: '9pt', marginTop: '0.25rem' }}>
                    {selectedJob.site}
                  </div>
                  <div style={{ color: 'grey', fontSize: '9pt' }}>
                    {selectedJob.client}
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fff3cd', borderRadius: '4px', color: '#856404' }}>
                  Please select a job from the list below
                </div>
              )}
              <EventContainerList
                selectedItem={selectedJob}
                setSelectedItem={setSelectedJob}
                onData={availableJobs}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                All day
              </label>
              <Switch
                checked={isCreateAllDay}
                onChange={(e) => setIsCreateAllDay(e.value)}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                <FaClock /> Start Time
              </label>
              <div style={{ width: '100%' }}>
                <DateTimePicker
                  value={createStartDate}
                  onChange={(e) => e.value && setCreateStartDate(e.value)}
                  format="dd/MM/yyyy HH:mm"
                  disabled={isCreateAllDay}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                <FaClock /> End Time
              </label>
              <div style={{ width: '100%' }}>
                <DateTimePicker
                  value={createEndDate}
                  onChange={(e) => e.value && setCreateEndDate(e.value)}
                  format="dd/MM/yyyy HH:mm"
                  disabled={isCreateAllDay}
                />
              </div>
            </div>

            {createSpansWeekend && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Includes weekends
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Switch
                    checked={createIncludesWeekends}
                    onChange={(e) => setCreateIncludesWeekends(e.value)}
                  />
                  <span style={{ color: 'grey', fontSize: '9pt' }}>
                    {createIncludesWeekends
                      ? 'Saturdays and Sundays will be booked too.'
                      : 'Saturdays and Sundays will be skipped.'}
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                <FaUser /> Employee
              </label>
              <input
                type="text"
                value={resources.find(r => r.employeeId === createEmployeeId)?.employeeName || ''}
                disabled
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '1rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5'
                }}
              />
            </div>
          </div>

          <DialogActionsBar>
            <Button
              themeColor="primary"
              onClick={handleCreateEvent}
              icon="save"
            >
              Create Event
            </Button>
            <Button
              onClick={() => {
                setCreateDialogVisible(false);
                setSelectedJob(null);
                setIsCreateAllDay(false);
                setCreateIncludesWeekends(false);
              }}
            >
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Tooltip */}
      {hoveredEvent && tooltipPosition && (() => {
        const TOOLTIP_HEIGHT_ESTIMATE = 300;
        const shouldFlip = tooltipPosition.y < TOOLTIP_HEIGHT_ESTIMATE;
        return (
        <div 
          className="event-tooltip"
          ref={(el) => {
            if (!el) return;
            const rect = el.getBoundingClientRect();
            if (rect.top < 8) {
              el.style.top = `${tooltipPosition.rectBottom + 10}px`;
              el.style.transform = 'translate(-50%, 0)';
            }
          }}
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: shouldFlip
              ? `${tooltipPosition.rectBottom + 10}px`
              : `${tooltipPosition.y - 10}px`,
            transform: shouldFlip ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
            pointerEvents: 'none'
          }}
        >
          <div className="tooltip-header">
            <strong>{hoveredEvent.jobRef}</strong>
          </div>
          <div className="tooltip-content">
            <div className="tooltip-row">
              <span className="tooltip-label"><FaBriefcase /> Job:</span>
              <span className="tooltip-value">{hoveredEvent.description}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label"><FaUser /> Client:</span>
              <span className="tooltip-value">{hoveredEvent.client}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label"><FaMapMarkerAlt /> Site:</span>
              <span className="tooltip-value">{hoveredEvent.siteAddr}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label"><FaUser /> Project Manager:</span>
              <span className="tooltip-value">{hoveredEvent.projectManager}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label"><FaClock /> Time:</span>
              <span className="tooltip-value">
                {moment(hoveredEvent.start).format('HH:mm')} - {moment(hoveredEvent.end).format('HH:mm')}
              </span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label"><FaCalendar /> Date:</span>
              <span className="tooltip-value">
                {moment(hoveredEvent.start).format('DD/MM/YYYY')}
              </span>
            </div>
            {(() => {
              const crew = getJobCrew(hoveredEvent.jobId, hoveredEvent.start);
              if (crew.length === 0) return null;
              return (
                <div className="tooltip-row">
                  <span className="tooltip-label"><FaUser /> Team:</span>
                  <span className="tooltip-value">
                    {crew.map(m => m.name).join(', ')}
                  </span>
                </div>
              );
            })()}
            <div className="tooltip-row">
              <span className="tooltip-label"><FaPlus /> Allocated:</span>
              <span className="tooltip-value">{formatAudit(hoveredEvent.createdBy, hoveredEvent.createdDate)}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label"><FaEdit /> Modified:</span>
              <span className="tooltip-value">{formatAudit(hoveredEvent.modifiedBy, hoveredEvent.modifiedDate)}</span>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default DiaryNew;
